import { ClassNameMap } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useStyling } from '../Theme';

/**
 * Style overrides for DataGrid list layout. Applied when listView prop is true.
 * Removes unwanted borders/outlines for DataGrid virtual scroller and cell elements.
 */
export const DATA_GRID_LIST_SX = {
    '& .MuiDataGrid-cell': {
        borderTop: 0
    },
    '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
        outline: 'none'
    },
    '& .MuiDataGrid-row:last-of-type': {
        borderBottom: 'none'
    }
};

export const useDataGridStyles = (): ClassNameMap<string> => {
    const { theme } = useStyling();
    return makeStyles({
        container: {
            height: '100%',
            width: '100%',
            backgroundColor: theme.palette.background.paper,

            '& .MuiDataGrid-root': {
                backgroundColor: theme.palette.background.paper
            },

            '& .MuiDataGrid-cell--pinnedRight': {
                backgroundColor: 'transparent',
                borderLeft: 'none'
            },
            '& .MuiDataGrid-columnHeader--last': {
                '& .MuiDataGrid-columnSeparator--sideLeft': {
                    color: 'transparent'
                }
            },
            '& .MuiDataGrid-actionsCell': {
                backgroundColor: theme.palette.background.paper,
                borderWidth: 1,
                borderStyle: 'solid',
                borderColor: theme.palette.divider,
                color: theme.palette.text.primary,
                borderRadius: '18px',
                padding: '4px'
            },
            '& .MuiDataGrid-row:hover': {
                backgroundColor: 'transparent',
                '& .MuiDataGrid-cell--pinnedRight': {
                    backgroundColor: 'transparent'
                }
            },
            '& .MuiDataGrid-row:not(.Mui-selected):not(.groupedRow):hover': {
                backgroundColor: 'inherit'
            },
            '& .MuiDataGrid-columnHeaderTitleContainer': {
                p: 0
            },
            '& .MuiInputBase-input': {
                pl: 1,
                pr: 1
            },
            '& .MuiDataGrid-columnHeader': {
                backgroundColor: theme.palette.background.paper
            },
            '& .MuiDataGrid-row--borderBottom .MuiDataGrid-scrollbarFiller': {
                borderBottom: 'none'
            },
            '& .MuiDataGrid-columnHeader--pinnedRight': {
                backgroundColor: 'transparent',
                borderLeft: 'none',
                pointerEvents: 'none'
            },
            '& .MuiDataGrid-columnHeaderTitle': {
                whiteSpace: 'break-spaces',
                lineHeight: 1.25
            },
            // Editing row has different styling
            '& .MuiDataGrid-row--editing': {
                backgroundColor: `darken(${theme.palette.background.default},10) !important`,
                // And cells are transparent
                '& .MuiDataGrid-cell': {
                    backgroundColor: 'transparent'
                }
            },
            '& .MuiDataGrid-cell': {
                minHeight: '43px'
            },
            '& .MuiDataGrid-groupingCriteriaCell': {
                fontWeight: 'bold',
                '& .MuiTypography-root': {
                    fontWeight: 'bold'
                },
                '& ._h': {
                    width: 'auto'
                }
            },
            '& .MuiDataGrid-cell.missingGroupingLabel .MuiDataGrid-groupingCriteriaCell': {
                fontStyle: 'italic',
                fontWeight: 'normal'
            },
            '& .MuiDataGrid-cell.missingGroupingLabel .MuiDataGrid-groupingCriteriaCell .MuiTypography-root': {
                fontStyle: 'italic',
                fontWeight: 'normal'
            },
            // Group member rows: line up the label with the group title, not the expand arrow.
            // 28px = MUI expand-icon width; theme.spacing(2) = gap after it (see GridRootStyles.ts#L849-L853).
            '& .MuiDataGrid-cell[data-field^="__row_group_by_columns_group"] > div:not(.MuiDataGrid-groupingCriteriaCell)':
                {
                    marginLeft: `calc(28px + ${theme.spacing(2)})`
                },
            '& .MuiDataGrid-cell--editing': {
                backgroundColor: theme.palette.background.default
            },
            '& .MuiDataGrid-cell--errors': {
                background: `linear-gradient(to top right,transparent 50%,${theme.palette.error.main} 50.1%) top right/10px 10px no-repeat,transparent;`
            }
        }
    })();
};
